import axios from 'axios';
import { useUserStore } from '@/stores/user';

import { toast } from 'vue3-toastify';

const mainAxios = axios.create({
    baseURL: `${import.meta.env.VITE_BACKEND_URL}`,
});

const noAuthAxios = axios.create({
    baseURL: `${import.meta.env.VITE_BACKEND_URL}`,
});

mainAxios.interceptors.request.use(
    (config) => {
        let token = useUserStore().get_session_token;

        if (!token) {
            token = localStorage.getItem('respeak-widget-token');
        }

        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        let origin = useUserStore().get_origin;
        if (origin) {
            config.headers['x-origin'] = origin;
        }

        const admin_mode = localStorage.getItem('admin_mode');
        if (admin_mode) {
            config.headers['superuser_mode'] = admin_mode;
        }

        // Add /v1 prefix to the URL unless it's in the exceptions list
        if (
            !config.url.startsWith('http') &&
            !config.url.startsWith('/v1') &&
            !config.url.startsWith('/oapi/') &&
            !config.url.startsWith('/expert/connectors/')
        ) {
            config.url = `/v1${config.url}`;
        }

        return config;
    },

    (error) => {
        return Promise.reject(error);
    },
);

// Add a response interceptor
mainAxios.interceptors.response.use(
    function (response) {
        return response;
    },
    async function (error) {
        const { config, response } = error;
        const userStore = useUserStore();

        // Handle network errors or server unavailable
        if (!response) {
            return Promise.reject(error);
        }

        // Handle token errors - skip if this is a refresh token request or already retried
        if (
            response.status === 403 &&
            response.headers['x-error'] === 'TOKEN-ERROR' &&
            !config.isRetry &&
            !config.skipAuthRetry &&
            !userStore.is_widget_user &&
            !localStorage.getItem('respeak-widget-token')
        ) {
            try {
                // Mark this request as a retry attempt
                config.isRetry = true;

                // Check if we're already in a refresh cycle
                if (userStore._refreshInProgress) {
                    console.log('Token refresh already in progress, waiting...');
                    // Wait for the existing refresh to complete
                    try {
                        const refreshSuccess = await userStore._refreshPromise;
                        // If successful, retry the original request
                        if (refreshSuccess) {
                            return mainAxios(config);
                        } else {
                            // Refresh completed but was unsuccessful
                            // No need to call logout here as the refresh method already did it
                            return Promise.reject(error);
                        }
                    } catch (e) {
                        // If refresh failed with exception, pass through to logout
                        await userStore.logout(
                            'Your session has expired. Please log in again to continue.',
                            'warning',
                        );
                        return Promise.reject(error);
                    }
                }

                // Try to refresh the token with special flag to prevent recursion
                const refreshed = await userStore.refresh_token(
                    userStore.credentials?.refresh_token,
                    true, // Skip auth retry for refresh token requests
                );

                if (refreshed) {
                    // If token refresh was successful, retry the original request
                    return mainAxios(config);
                } else {
                    // If refresh failed but we didn't throw yet, force logout
                    await userStore.logout(
                        'Your session has expired. Please log in again to continue.',
                        'warning',
                    );
                    return Promise.reject(error);
                }
            } catch (refreshError) {
                // Any failure during refresh should log the user out
                await userStore.logout(
                    'Your session has expired. Please log in again to continue.',
                    'warning',
                );
                return Promise.reject(error);
            }
        }

        // Handle unauthorized with admin fallback
        if (response.status === 401 && !config.isAdminRetry) {
            if (userStore.set_admin_mode(true)) {
                toast.warn('Retry with admin mode');
                config.isAdminRetry = true;
                return mainAxios(config);
            }
        }

        return Promise.reject(error);
    },
);

// add v1 prefix to the noAuthAxios
noAuthAxios.interceptors.request.use(
    (config) => {
        // Add /v1 prefix to the URL unless it's in the exceptions list
        if (!config.url.startsWith('/v1')) {
            config.url = `/v1${config.url}`;
        }

        return config;
    },

    (error) => {
        return Promise.reject(error);
    },
);

export { mainAxios, noAuthAxios };
